import { ref, computed, watch, onScopeDispose } from "vue";
import { api } from "@/loanLinkApi";
import { ContentType } from "@/generatedApi";
import { useFeatureFlag } from "./useFeatureFlag";

export interface ExtractionSuggestion {
	id: string;
	documentId: string;
	documentName: string | null;
	classificationLabel: string;
	sectionType: string;
	matchedCustomerId: string | null;
	matchSource: string;
	matchConfidence: number;
	status: string;
	reactivatedAfterDelete?: boolean;
	createdAt: string;
}

export function useExtractionSuggestions(dossierId: () => string | undefined) {
	const suggestions = ref<ExtractionSuggestion[]>([]);
	const loading = ref(false);
	const extractionEnabled = useFeatureFlag("document-extraction-intermediary");

	let generation = 0;
	onScopeDispose(() => {
		generation++;
	});
	async function fetchSuggestions() {
		const id = dossierId();
		const requestGeneration = ++generation;
		suggestions.value = [];
		if (!id || !extractionEnabled.value) {
			suggestions.value = [];
			loading.value = false;
			return;
		}

		loading.value = true;
		try {
			const { data } = await api.request<{
				suggestions: ExtractionSuggestion[];
			}>({
				path: `/dossier/${id}/extraction-suggestions`,
				method: "GET",
				secure: true,
				format: "json",
			});
			if (requestGeneration === generation)
				suggestions.value = data.suggestions;
		} catch {
			if (requestGeneration === generation) suggestions.value = [];
		} finally {
			if (requestGeneration === generation) loading.value = false;
		}
	}

	async function markBekeken(suggestionId: string) {
		const id = dossierId();
		if (!id) return;

		const suggestion = suggestions.value.find((s) => s.id === suggestionId);
		if (!suggestion || suggestion.status !== "nieuw") return;

		const previousStatus = suggestion.status;
		suggestion.status = "bekeken";
		try {
			await api.request({
				path: `/dossier/${id}/extraction-suggestions/${suggestionId}`,
				method: "PATCH",
				secure: true,
				type: ContentType.Json,
				format: "json",
				body: { status: "bekeken" },
			});
		} catch {
			suggestion.status = previousStatus;
		}
	}

	async function dismiss(suggestionId: string, reason?: string) {
		const id = dossierId();
		if (!id) return;

		const suggestion = suggestions.value.find((s) => s.id === suggestionId);
		if (!suggestion) return;

		const previousStatus = suggestion.status;
		suggestion.status = "genegeerd";
		try {
			await api.request({
				path: `/dossier/${id}/extraction-suggestions/${suggestionId}`,
				method: "PATCH",
				secure: true,
				type: ContentType.Json,
				format: "json",
				body: { status: "genegeerd", dismissReason: reason },
			});
		} catch {
			suggestion.status = previousStatus;
		}
	}

	function forCustomerAndSection(
		customerId: string | undefined,
		sectionType: string,
	) {
		return computed(() =>
			suggestions.value.filter(
				(s) =>
					s.sectionType === sectionType &&
					s.matchedCustomerId === customerId &&
					s.matchedCustomerId !== null &&
					s.status !== "genegeerd" &&
					s.status !== "vervangen",
			),
		);
	}

	function unmatchedForSection(sectionType: string) {
		return computed(() =>
			suggestions.value.filter(
				(s) =>
					s.sectionType === sectionType &&
					s.matchedCustomerId === null &&
					s.status !== "genegeerd" &&
					s.status !== "vervangen",
			),
		);
	}

	function allUnmatched() {
		return computed(() =>
			suggestions.value.filter(
				(s) =>
					s.matchedCustomerId === null &&
					s.status !== "genegeerd" &&
					s.status !== "vervangen",
			),
		);
	}

	watch(dossierId, fetchSuggestions, { immediate: true, flush: "sync" });
	watch(extractionEnabled, fetchSuggestions, { flush: "sync" });

	return {
		suggestions,
		loading,
		fetchSuggestions,
		markBekeken,
		dismiss,
		forCustomerAndSection,
		unmatchedForSection,
		allUnmatched,
	};
}
